import { createApi } from '@reduxjs/toolkit/query/react';
import { ReleasesType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';

type WorkbookSheetCandidate = {
  row_index: number;
  score: number;
  non_empty_count: number;
  preview: string[];
};

type WorkbookSheetInfo = {
  name: string;
  index: number;
  detected_header_row_index: number;
  header_candidates: WorkbookSheetCandidate[];
  headers: string[];
  is_recommended: boolean;
};

type WorkbookInspectResponse = {
  sheet_names: string[];
  recommended_sheet_name: string;
  recommended_header_row_index: number;
  sheets: WorkbookSheetInfo[];
};

type BOMMappingWithMeta = ReleasesType.BOMMappings & {
  id: number;
  mapping_type: ReleasesType.BomMappingType;
  sheet_name: string;
  header_row_index: number;
};

type patchReleaseArgs = {
  number?: string;
  project?: number;
  // Release-level due date (ISO date, e.g. "2026-10-01"). Setting this also
  // propagates to every FabricationWorkOrder under this release.
  due_date?: string | null;
};

type postReleaseArgs = {
  project: number;
  number: string;
  bom_mapping: number;
  bom_file: string;
  frame_qty_file?: string;
  // Preferred: a saved FRAME_BOM mapping id. The backend snapshots it onto the
  // release. frame_field_mapping is kept for backwards compatibility only.
  frame_bom_mapping?: number;
  frame_field_mapping?: ReleasesType.FrameFieldMapping;
  due_date?: string | null;
  replaces_release?: number;
  replacement_reason?: string;
};

type args = {
  postRelease: postReleaseArgs;
  getReleaseById: {
    releaseId: number;
  };
  getAllRelease: {
    project?: number;
    include_inactive?: boolean;
    include_dependencies?: boolean;
  } | void;
  patchRelease: {
    releaseId: number;
    body: patchReleaseArgs;
  };
  deleteRelease: {
    releaseId: number;
  };
  archiveRelease: {
    releaseId: number;
    body: { reason: string };
  };
  getBomAudit: {
    project?: number;
  } | void;
  postBomMappingGetHeaders: {
    file_url: string;
    module?: 'bom' | 'frame_qty';
  };
  reprocessReleaseFrames: {
    releaseId: number;
    body: {
      frame_qty_file?: string;
      frame_bom_mapping?: number;
      frame_field_mapping?: ReleasesType.FrameFieldMapping;
    };
  };
  postBomMappings: ReleasesType.BOMMappings & {
    mapping_type?: ReleasesType.BomMappingType;
    sheet_name: string;
    header_row_index: number;
  };
  patchBomMapping: {
    id: number;
    body: {
      name: string;
      mapping: any;
      mapping_type?: ReleasesType.BomMappingType;
      sheet_name: string;
      header_row_index: number;
    };
  };
  deleteBomMapping: {
    id: number;
  };
  getBomMappings: {
    mapping_type?: ReleasesType.BomMappingType;
  } | void;
  getSummaryViews: {
    releaseId: number;
  };
};

type responses = {
  postRelease: ReleasesType.Release;
  getAllRelease: ReleasesType.Release[];
  getReleaseById: ReleasesType.Release;
  patchRelease: ReleasesType.Release;
  deleteRelease: { detail: string };
  archiveRelease: ReleasesType.Release;
  getBomAudit: ReleasesType.BomAuditEvent[];
  postBomMappingGetHeaders: WorkbookInspectResponse;
  reprocessReleaseFrames: {
    detail: string;
    summary: {
      created: number;
      updated: number;
      skipped_totals: number;
      skipped_assigned: number;
    };
  };
  postBomMappings: BOMMappingWithMeta;
  getBomMappings: BOMMappingWithMeta[];
  patchBomMapping: BOMMappingWithMeta;
  deleteBomMapping: void;
  getSummaryViews: ReleasesType.SummaryView[];
};

export const releasesApi = createApi({
  reducerPath: 'releasesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Releases'],

  endpoints: (builder) => ({
    postRelease: builder.mutation<
      responses['postRelease'],
      args['postRelease']
    >({
      query: ({
        project,
        number,
        bom_mapping,
        bom_file,
        frame_qty_file,
        frame_bom_mapping,
        frame_field_mapping,
        due_date,
        replaces_release,
        replacement_reason,
      }) => ({
        url: '/core/release/',
        method: 'POST',
        body: {
          project,
          number,
          bom_mapping,
          bom_file,
          frame_qty_file,
          frame_bom_mapping,
          frame_field_mapping,
          due_date,
          replaces_release,
          replacement_reason,
        },
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Releases' }],
    }),

    reprocessReleaseFrames: builder.mutation<
      responses['reprocessReleaseFrames'],
      args['reprocessReleaseFrames']
    >({
      query: ({ releaseId, body }) => ({
        url: `/core/release/${releaseId}/reprocess-frames/`,
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: (_result, _error, _args) => [
        { type: 'Releases' },
        { type: 'Releases', id: `summary-views-release-${_args.releaseId}` },
      ],
    }),

    getAllRelease: builder.query<
      responses['getAllRelease'],
      args['getAllRelease']
    >({
      query: (params) => ({
        url: `/core/release/`,
        method: 'GET',
        params: params ?? undefined,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor: 3600,
      providesTags: (_result) =>
        _result ? [{ type: 'Releases', id: 'all' }] : [],
    }),

    getReleaseById: builder.query<
      responses['getReleaseById'],
      args['getReleaseById']
    >({
      query: ({ releaseId }) => ({
        url: `/core/release/${releaseId}/`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _args) =>
        _result ? [{ type: 'Releases', id: _args.releaseId }] : [],
    }),

    patchRelease: builder.mutation<
      responses['patchRelease'],
      args['patchRelease']
    >({
      query: ({ releaseId, body }) => ({
        url: `/core/release/${releaseId}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Releases' }],
    }),

    deleteRelease: builder.mutation<void, args['deleteRelease']>({
      query: ({ releaseId }) => ({
        url: `/core/release/${releaseId}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_result, _error, _arg) => ['Releases'],
    }),

    archiveRelease: builder.mutation<
      responses['archiveRelease'],
      args['archiveRelease']
    >({
      query: ({ releaseId, body }) => ({
        url: `/core/release/${releaseId}/archive/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Releases' }],
    }),

    getBomAudit: builder.query<
      responses['getBomAudit'],
      args['getBomAudit']
    >({
      query: (params) => ({
        url: '/core/release-bom-audit/',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'Releases', id: 'bom-audit' }],
    }),

    postBomMappingGetHeaders: builder.mutation<
      responses['postBomMappingGetHeaders'],
      args['postBomMappingGetHeaders']
    >({
      query: (body) => ({
        url: '/core/bom-mappings/inspect-workbook/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
    }),

    postBomMappings: builder.mutation<
      responses['postBomMappings'],
      args['postBomMappings']
    >({
      query: (body) => ({
        url: '/core/bom-mappings/',
        method: 'POST',
        body,
        validateStatus: (response, result) =>
          response.status === 201 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Releases', id: 'bom-mappings' }],
    }),

    patchBomMapping: builder.mutation<
      responses['patchBomMapping'],
      args['patchBomMapping']
    >({
      query: ({ id, body }) => ({
        url: `/core/bom-mappings/${id}/`,
        method: 'PATCH',
        body,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      invalidatesTags: [{ type: 'Releases', id: 'bom-mappings' }],
    }),

    deleteBomMapping: builder.mutation<
      responses['deleteBomMapping'],
      args['deleteBomMapping']
    >({
      query: ({ id }) => ({
        url: `/core/bom-mappings/${id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: [{ type: 'Releases', id: 'bom-mappings' }],
    }),

    getBomMappings: builder.query<
      responses['getBomMappings'],
      args['getBomMappings']
    >({
      query: (params) => ({
        url: '/core/bom-mappings/',
        method: 'GET',
        params: params?.mapping_type
          ? { mapping_type: params.mapping_type }
          : undefined,
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result) =>
        _result ? [{ type: 'Releases', id: 'bom-mappings' }] : [],
    }),

    getSummaryViews: builder.query<
      responses['getSummaryViews'],
      args['getSummaryViews']
    >({
      query: ({ releaseId }) => ({
        url: `/core/release/${releaseId}/summary-view`,
        method: 'GET',
        validateStatus: (response, result) =>
          response.status === 200 && !result.isError,
      }),
      keepUnusedDataFor,
      providesTags: (_result, _error, _args) =>
        _result
          ? [
              {
                type: 'Releases',
                id: `summary-views-release-${_args.releaseId}`,
              },
              {
                type: 'Releases',
                id: 'frame-drawing-summaries',
              },
            ]
          : [],
    }),
  }),
});

export const {
  usePostReleaseMutation,
  useGetAllReleaseQuery,
  useGetReleaseByIdQuery,
  usePatchReleaseMutation,
  useDeleteReleaseMutation,
  useArchiveReleaseMutation,
  useGetBomAuditQuery,
  usePostBomMappingGetHeadersMutation,
  useReprocessReleaseFramesMutation,
  usePostBomMappingsMutation,
  useGetBomMappingsQuery,
  useGetSummaryViewsQuery,
  usePatchBomMappingMutation,
  useDeleteBomMappingMutation,
} = releasesApi;

export namespace ReleasesApi {
  export const Api = releasesApi;
  export type Args = args;
  export type Responses = responses;
}
